'use client';

import HeaderMenuContent from '@/components/sections/builder/elements/header/header/header-menu-content';
import { HeaderElementDataType } from '@/components/sections/builder/elements/header/header/type';
import { ElementProps } from '@/components/sections/builder/types';
import { useLocationMultiOrigin, useMenu } from '@/hooks/api/storefront/menu-list';
import useModal from '@/hooks/useModal';
import { cn } from '@/lib/utils';
import { IMenuItem } from '@/types/navigation';
import Cookies from 'js-cookie';
import { MapPin, Search, XIcon } from 'lucide-react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';

import { UserNavMobile } from '@/components/sections/builder/elements/header/header/user-nav-mobile';
import ModalSearch from '@/components/sections/builder/search-modal/search-modal';
import { ThemeType } from '@/stores/builder';
import { useCookies } from '@/app/cookie-provider';
import { deleteLocationMultiOrigin } from '@/lib/actions/cart/action';
import { DrawerSelectLocation } from '@/components/sections/builder/elements/body/drawer-select-location';
import { MultiOriginLocation, StoreOrderLocationPickup } from '@/types/http';

import HeaderLogo from '@/components/sections/builder/elements/header/header/header-logo';
import { MegaNavbar } from '@/components/sections/builder/elements/header/header/content/navbar';
import { Input } from '@/components/ui/input';

export default function HeaderContent({ element, preview, theme }: ElementProps) {
  const pathname = usePathname();
  const token = Cookies.get('accessToken');

  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const tokenPreview = query.get('token') || '';
  const { multiOrigin } = useCookies();
  const modalMultiOrigin = useModal();

  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [openMenuId, setOpenMenuId] = useState<string>('');
  const [openSubMenuId, setOpenSubMenuId] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<
    MultiOriginLocation | StoreOrderLocationPickup | undefined
  >();
  const [coordinates, setCoordinates] = useState<{ lat: number; long: number } | null>({
    lat: -6.216601629606373,
    long: 106.8182250518579,
  });

  const [headerFixed, setHeaderFixed] = useState<boolean>(false);
  const [headerStatic, setHeaderStatic] = useState<boolean>(true);
  const [annInView, setAnnInView] = useState<boolean>(true);
  const [measured, setMeasured] = useState({
    wrapperHeader: 0,
    headerHeight: 0,
    stickyTop: 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const el = document.getElementById('announcement');
    if (!el) {
      setAnnInView(false);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        setAnnInView(entry.isIntersecting && entry.intersectionRatio > 0);
      },
      { threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const searchModal = useModal();
  const data = element.data as HeaderElementDataType;

  const isModalSearch =
    data.searchBarPosition === 'modal' ||
    data.searchBarPosition === '' ||
    data.searchBarPosition === undefined;
  const isStickyMode = data.stickyHeader === 'on-scroll-up' || data.stickyHeader === 'always';
  const stickyEnabledNow = isStickyMode && !annInView;

  const isHeaderVisible = !stickyEnabledNow
    ? true
    : data.stickyHeader === 'always' || headerStatic || headerFixed;

  const headerHeight = measured?.headerHeight || 0;
  const sticky = measured?.stickyTop || 0;
  const wrapperHeader = measured?.wrapperHeader || 0;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const measure = () => {
      const headerEl = document.getElementById('header-sticky');
      const wrapperH = document.getElementById('wrapper-header')?.offsetHeight || 0;
      const hdrH = headerEl?.offsetHeight || 0;
      const stickyTop = Number(headerEl?.offsetTop || 0);

      setMeasured({
        wrapperHeader: wrapperH,
        headerHeight: hdrH,
        stickyTop,
      });
    };

    measure();
    window.addEventListener('resize', measure);
    requestAnimationFrame(measure);

    return () => {
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!stickyEnabledNow) {
      setHeaderFixed(false);
      setHeaderStatic(true);
      return;
    }

    const spacing = data.spacing || 0;

    const pinThreshold = sticky + wrapperHeader - spacing;
    const topStaticBoundary = Math.max(0, wrapperHeader - headerHeight - spacing);
    const DELTA = 8;

    const lastYRef = { current: window.scrollY };
    const pinnedRef = { current: false };

    if (data.stickyHeader === 'always') {
      setHeaderFixed(true);
      setHeaderStatic(false);
    }

    const onScroll = () => {
      if (data.stickyHeader === 'on-scroll-up') {
        const currentY = window.scrollY;
        const prevY = lastYRef.current;
        const goingUp = currentY < prevY - 1;
        const goingDown = currentY > prevY + 1;

        const shouldStatic = currentY <= topStaticBoundary;
        setHeaderStatic((prev) => (prev !== shouldStatic ? shouldStatic : prev));

        if (!shouldStatic) {
          if (goingUp && currentY > pinThreshold + DELTA && !pinnedRef.current) {
            pinnedRef.current = true;
            setHeaderFixed(true);
          }

          if (goingDown && pinnedRef.current && currentY >= prevY + DELTA) {
            pinnedRef.current = false;
            setHeaderFixed(false);
          }
        } else {
          if (pinnedRef.current) {
            pinnedRef.current = false;
            setHeaderFixed(false);
          }
        }

        lastYRef.current = currentY;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [stickyEnabledNow, data.stickyHeader, data.spacing, wrapperHeader, headerHeight, sticky]);

  const { data: menuData, isLoading: isLoadingMenu } = useMenu(data.menuId, {
    enabled: !!data.menuId && preview,
  });
  const { data: locationData } = useLocationMultiOrigin({
    enabled: multiOrigin && !preview && pathname !== '/checkout',
  });

  const isMegaMenu =
    typeof menuData?.is_mega_menu === 'string'
      ? menuData.is_mega_menu === 'true'
      : !!menuData?.is_mega_menu;

  const menuItems = menuData?.menu_items || [];

  const selectedLocationId = Cookies.get('_location_id');
  const hasLocations = Array.isArray(locationData) && locationData.length > 0;
  const locationToDisplay = selectedLocation ?? (hasLocations ? locationData[0] : null);

  useEffect(() => {
    if (multiOrigin === false && selectedLocationId) {
      deleteLocationMultiOrigin();
    }
  }, [multiOrigin, selectedLocationId]);

  useEffect(() => {
    if (!selectedLocation && hasLocations && pathname !== '/checkout') {
      const fallback =
        locationData.find((loc) => loc.location_id === selectedLocationId) ||
        locationData.find((loc) => loc.is_default) ||
        locationData[0];

      setSelectedLocation(fallback);
    }
  }, [locationData, hasLocations]);

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-[160] block transform overflow-hidden bg-gray-900 bg-opacity-25 ease-in-out lg:hidden ',
          !collapsed
            ? '-translate-x-0 opacity-100 transition-opacity'
            : '-translate-x-full opacity-0 transition-all'
        )}
      >
        <div
          className={cn(
            'absolute left-0 h-full w-full transform bg-white text-left shadow-xl transition-all delay-200 duration-200 ease-in-out',
            !collapsed ? '-translate-x-0' : '-translate-x-full'
          )}
        >
          <div
            className={cn(
              'relative flex h-full w-full flex-col space-y-1 overflow-y-auto p-6',
              token ? 'pb-36' : 'pb-20'
            )}
          >
            <div className='mb-4 flex justify-between'>
              {data.logoUrl && (
                <Suspense fallback={<div>Loading...</div>}>
                  <HeaderLogo {...data} />
                </Suspense>
              )}
              <button type='button' onClick={() => setCollapsed(true)}>
                <XIcon className='h-6 w-6' />
              </button>
            </div>

            <nav aria-label='Header Navigation'>
              <ul>
                {menuItems.map((menu) => (
                  <li key={menu.id} className='flex flex-shrink-0 md:flex-wrap'>
                    <HeaderMenuContent
                      menu={menu as IMenuItem}
                      isMegaMenu={isMegaMenu}
                      openMenuId={openMenuId || ''}
                      setOpenMenuId={setOpenMenuId}
                      openSubMenuId={openSubMenuId || ''}
                      setOpenSubMenuId={setOpenSubMenuId}
                    />
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          <UserNavMobile token={token} onClose={() => setCollapsed(true)} />
        </div>
        <div className='h-full w-full cursor-pointer' onClick={() => setCollapsed(true)}></div>
      </div>

      {stickyEnabledNow && <div aria-hidden style={{ height: `${headerHeight}px` }} />}

      <div
        id='header-sticky'
        style={{ top: stickyEnabledNow ? 0 : undefined }}
        className={cn(
          'padding-header color-scheme-header inset-x-0 z-[50] w-full pb-3 text-sm md:py-4 lg:!px-4 lg:pb-0',
          stickyEnabledNow &&
            'fixed transition-[transform,opacity] duration-300 ease-out will-change-transform',
          stickyEnabledNow
            ? isHeaderVisible
              ? 'translate-y-0 opacity-100'
              : '-translate-y-full opacity-0'
            : 'opacity-100',
          {
            'justify-center md:justify-between':
              isModalSearch && ['top-center'].includes(data.logoPosition as string),
            'justify-start md:justify-between':
              isModalSearch && !['top-center'].includes(data.logoPosition as string),
            'items-center md:items-start md:justify-between':
              isModalSearch && ['top-left', 'top-center'].includes(data.logoPosition as string),
            'items-center':
              isModalSearch && !['top-left', 'top-center'].includes(data.logoPosition as string),
          }
        )}
      >
        <MegaNavbar
          data={data}
          menuItems={menuItems}
          isMegaMenu={isMegaMenu}
          preview={preview}
          searchModal={searchModal}
          token={token}
          themeId={themeId}
          tokenPreview={tokenPreview}
          theme={theme}
          setCollapsedAction={setCollapsed}
          modalMultiOrigin={modalMultiOrigin}
          selectedLocation={selectedLocation}
          locationToDisplay={locationToDisplay}
          multiOrigin={multiOrigin}
          isLoadingMenu={isLoadingMenu}
        />

        {data.showSearch && data.searchBarPosition === 'header' && (
          <div className='block px-5 py-3 md:hidden'>
            <Input
              name='keyword'
              type='text'
              data-test-id='header-search-input'
              placeholder='Search...'
              className={cn(
                'theme_input h-2 w-full py-4 placeholder:text-[--theme-input-color] lg:flex-none'
              )}
              onClick={() => searchModal.open()}
              classNameWrapper='w-full lg:w-auto'
              rightContent={
                <button
                  type='button'
                  className={cn('', data.logoPosition === 'middle-left' && 'lg:hidden')}
                >
                  <Search className='h-4 w-4' />
                </button>
              }
            />
          </div>
        )}

        {multiOrigin && (
          <div
            className={cn('flex w-full items-center justify-center gap-x-2 pb-5 pt-2 lg:hidden')}
          >
            <div
              role='button'
              onClick={() => {
                modalMultiOrigin?.open();
              }}
              className='flex w-full items-center justify-center gap-x-2'
            >
              <MapPin className='h-5 w-5' />
              <span className='whitespace-nowrap font-medium underline-offset-4 hover:underline'>
                {selectedLocation || locationToDisplay
                  ? selectedLocation
                    ? selectedLocation.location_name ?? ''
                    : locationToDisplay?.location_name ?? ''
                  : 'Delivery from'}
              </span>
            </div>
          </div>
        )}
      </div>

      <ModalSearch
        openModal={searchModal.isShow}
        setOpenModal={searchModal.setIsShow}
        host={typeof window !== 'undefined' ? window.location.host : ''}
        locationId={selectedLocationId}
        preview={!!preview}
        theme={theme as ThemeType}
      />

      <DrawerSelectLocation
        openModal={modalMultiOrigin.isShow}
        setOpenModal={modalMultiOrigin.setIsShow}
        locations={locationData || []}
        theme={theme as ThemeType}
        preview={preview}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
        coordinates={coordinates}
        setCoordinates={setCoordinates}
      />

      <style jsx global>
        {`
          @media (min-width: 768px) {
            .padding-header {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${(data.styleWidth !== 'box' ? data.padding?.left : preview && 20) ||
              0}px;
              padding-right: ${(data.styleWidth !== 'box' ? data.padding?.right : preview && 20) ||
              0}px;
            }
          }
          .offset-top {
            top: -${headerHeight}px;
          }
          .header-height {
            padding-top: ${headerHeight}px;
          }
          .header-spacing {
            height: ${data.spacing}px;
          }
          .color-scheme-header {
            background-color: ${data?.colorSchemeHeader?.background || '#ffffff'};
            color: ${data.colorSchemeHeader?.text || '#000000'};
          }
          .header-full-width {
            max-width: unset;
            width: 100%;
          }
          .gap-navigation {
            gap: ${data.spacingNav || 8}px;
          }
          @media (prefers-reduced-motion: reduce) {
            .transition-[transform,opacity] {
              transition: none !important;
            }
          }
        `}
      </style>
    </>
  );
}
